// Simulate as3 Event static event names
import Rectangle = createjs.Rectangle;
import DisplayObject = createjs.DisplayObject;
import { CoreLoggerConfigurator, LoggingLevel, getLogger } from "@goodgamestudios/logger";
import {cacheModules} from "../createjs-utilities/utils/getDefinitionByName";

const logger = getLogger("createjs.ts Extensions");

// prepares a cache of available modules for getDefinitionByName
createjs.precacheModules = cacheModules;



// Simulate as3 TimerEvent static event names
createjs.TimerEvent = class TimerEvent extends createjs.Event {
    public static TIMER = "timer";
    public static TIMER_COMPLETE = "timerComplete";
};

// Simulate as3 IOErrorEvent static event names
createjs.IOErrorEvent = class IOErrorEvent extends createjs.Event {
    public static IO_ERROR = "error";
    text: string;
};

// Simulate as3 IOErrorEvent static event names
createjs.SecurityErrorEvent = class SecurityErrorEvent extends createjs.Event {
    public static SECURITY_ERROR = "error";
    text: string;
};

// simulate AS3 Error class
Object.defineProperty(Error.prototype, "errorID", {
    configurable: false,
    enumerable: true,
    value: 0,
    writable: false,
});

// Used for "COMPLETE" events on ToolBoxJS
createjs.LoaderEvent = class LoaderEvent extends createjs.Event {
    url: string;
};

// Simulate as3 ProgressEvent static event names
createjs.ProgressEvent.PROGRESS = "progress";

type StageWithProgressProperties = createjs.Stage & { bytesTotal: number, bytesLoaded: number, __quality: number }

// Simulate as3 ProgressEvent properties
Object.defineProperty(createjs.Stage.prototype, "bytesTotal", {
    get: function (this: StageWithProgressProperties): number {
        return this.bytesTotal;
    },
    set: function (this: StageWithProgressProperties, value: number) {
        this.bytesTotal = value;
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 ProgressEvent properties
Object.defineProperty(createjs.Stage.prototype, "bytesLoaded", {
    get: function (this: StageWithProgressProperties): number {
        return this.bytesLoaded;
    },
    set: function (this: StageWithProgressProperties, value: number) {
        this.bytesLoaded = value;
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 ProgressEvent properties
Object.defineProperty(createjs.Stage.prototype, "quality", {
    get: function (this: StageWithProgressProperties): number {
        if (!this.__quality) {
            this.__quality = 1;
        }
        return this.__quality;
    },
    set: function (this: StageWithProgressProperties, value: number) {
        this.__quality = value;
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 TextEvent static event names
// TODO: Why is 'any' necessary only here?
(<any>createjs).TextEvent = class TextEvent extends createjs.Event {
    constructor(type: string, bubbles: boolean = false, cancelable: boolean = false, text: string = "") {
        super(type, bubbles, cancelable);
        this.text = text;
    }
    public static INPUT_TEXT = "input";
    public static LINK = "link";
    public text = "";
};

// Simulate as3 FocusEvent static event names
createjs.FocusEvent = class FocusEvent extends createjs.Event {
    public static FOCUS_IN = "focusIn";
    public static FOCUS_OUT = "focusOut";
    public static KEY_FOCUS_CHANGE = "keyFocusChange";
};

// Simulate as3 StageQuality class
createjs.StageQuality = class StageQuality {
    public static LOW = "low";
    public static MEDIUM = "medium";
    public static HIGH = "high";
    public static BEST = "best";
};

/**
 * Point
 */

// Simulate AS3 point.length
Object.defineProperty(createjs.Point.prototype, "length", {
    get: function (this: createjs.Point) {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    },
    enumerable: true,
    configurable: true
});

createjs.Point.prototype.equals = function (toCompare: createjs.Point): boolean {
    return this.x == toCompare.x && this.y == toCompare.y;
};

createjs.Point.prototype.add = function (v: createjs.Point): createjs.Point {
    return new createjs.Point(this.x + v.x, this.y + v.y);
};

createjs.Point.prototype.offset = function (dx: number, dy: number): createjs.Point {
    return this.add(new createjs.Point(dx, dy));
};

createjs.Point.prototype.subtract = function (v: createjs.Point): createjs.Point {
    return new createjs.Point(this.x - v.x, this.y - v.y);;
};

createjs.Point.prototype.normalize = function (thickness: number): void {
    if (this.x != 0 || this.y != 0) {
        let relativeThickness: number = thickness / this.length;
        this.x *= relativeThickness;
        this.y *= relativeThickness;
    }
};

/**
 * Determines a point between two specified points.
 * The parameter f determines where the new interpolated point is located relative to the two end points specified by
 * parameters p1 and p2. The closer the value of the parameter f is to 1.0, the closer the interpolated point is to
 * the first point (parameter p1). The closer the value of the parameter f is to 0, the closer the interpolated point
 * is to the second point (parameter p2).
 *
 * @param p1 The first point.
 * @param p2 The second point.
 * @param f The level of interpolation between the two points. Indicates where the new point will be,
 * along the line between p1 and p2. If f=1, p1 is returned; if f=0, p2 is returned.
 * @returns The new interpolated point.
 */
createjs.Point.interpolate = function (p1: createjs.Point, p2: createjs.Point, f: number): createjs.Point {
    let f1: number = 1 - f;
    return new createjs.Point(p1.x * f + p2.x * f1, p1.y * f + p2.y * f1);
};

/**
 * Returns the distance between p1 and p2.
 *
 * @param p1 The first point.
 * @param p2 The second point.
 * @returns The distance between the first and second points.
 */
createjs.Point.distance = function (p1: createjs.Point, p2: createjs.Point): number {
    return Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y));
};

Object.defineProperty(createjs.Point.prototype, "right", {
    get: function (this: createjs.Point) {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    },
    set: function (this: createjs.Point, value) { // TODO: What could possibly be the point of this?
        return Math.sqrt(this.x * this.x + this.y * this.y);
    },
    enumerable: true,
    configurable: true
});

/**
 * Rectangle // TODO: What is the point of extending `Point` like a `Rectangle`?
 */
Object.defineProperty(createjs.Point.prototype, "right", {
    get: function (this: any) {
        return this.x + this.width;
    },
    set: function (this: any, value) {
        this.width = value - this.x;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Point.prototype, "bottom", {
    get: function (this: any) {
        return this.y + this.height;
    },
    set: function (this: any, value) {
        this.height = value - this.y;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Point.prototype, "left", {
    get: function (this: any) {
        return this.x;
    },
    set: function (this: any, value) {
        this.width += this.x - value;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Point.prototype, "top", {
    get: function (this: any) {
        return this.y;
    },
    set: function (this: any, value) {
        this.height += this.y - value;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Point.prototype, "topLeft", {
    get: function (this: any) {
        return new createjs.Point(this.left, this.top);
    },
    set: function (this: any, value: createjs.Point) {
        this.top = value.y;
        this.left = value.x;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Point.prototype, "bottomRight", {
    get: function (this: any) {
        return new createjs.Point(this.right, this.bottom);
    },
    set: function (this: any, value: createjs.Point) {
        this.bottom = value.y;
        this.right = value.x;
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 object.stageWidth, uses canvas object
Object.defineProperty(createjs.Stage.prototype, "stageWidth", {
    get: function (this: createjs.Stage) {
        return this.canvas ? Math.round(this.canvas.width / this.scaleX) : 0;
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 object.stageHeight, uses canvas object
Object.defineProperty(createjs.Stage.prototype, "stageHeight", {
    get: function (this: createjs.Stage) {
        return this.canvas ? Math.round(this.canvas.height / this.scaleY) : 0;
    },
    enumerable: true,
    configurable: true
});

type StageWithColor = createjs.Stage & { _color: string };

// Mockup for as3 stage.color
Object.defineProperty(createjs.Stage.prototype, "color", {
    get: function (this: StageWithColor) {
        if (!this._color) {
            this._color = "transparent";
        }
        return this._color;
    },
    set: function (this: StageWithColor, value: string) {
        this._color = value;
        this.canvas.style.backgroundColor = this._color;
    },
    enumerable: true,
    configurable: true
});


Object.defineProperty(createjs.MouseEvent.prototype, "relatedObject", {
    get: function (this: createjs.MouseEvent): DisplayObject {
        return this.nativeEvent.relatedTarget as any;
    },
    set: function (this: createjs.MouseEvent, value: DisplayObject) {
        (this.nativeEvent as any).relatedTarget = value;
    },
    enumerable: true,
    configurable: true
});


// Simulate as3 Sprite

type StageWithMouseXY = createjs.Stage & { _mouseX: number, _mouseY: number };

Object.defineProperty(createjs.Stage.prototype, "mouseX", {
    get: function (this: StageWithMouseXY): number {
        return this._mouseX;
    },
    set: function (this: StageWithMouseXY, value: number) {
        this._mouseX = value;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(createjs.Stage.prototype, "mouseY", {
    get: function (this: StageWithMouseXY): number {
        return this._mouseY;
    },
    set: function (this: StageWithMouseXY, value: number) {
        this._mouseY = value;
    },
    enumerable: true,
    configurable: true
});
